import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { HttpInterceptorService } from '../../../../core/services/http-interceptor.service';
import { Observable } from 'rxjs/Observable';
import { DiagnosticOrderActionsService } from '../../services/diagnostic-order-actions.service';
import { IDiagnosticOrder } from '../../interfaces/diagnostic-order.interface';
import { DynamicModalLoaderService } from '../../../../layout/services/dynamic-modal-loader.service';
import { PcrGraphComponent } from '../pcr-graph/pcr-graph.component';
import { PlaceholderModalComponent } from '../../../../layout/components/modals/placeholder-modal/placeholder-modal.component';
import { PdfService } from '@ispm/test/services/pdf.service';
import { Store } from '@ngrx/store';
import { selectCurrentConfig } from '../../../../configuration/store/configuration.selector';
import { map, takeWhile } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import * as lodash from 'lodash';
import { CoreActionCreatorService } from '@ispm/core/services/core-action-creator.service';
import { DiagnosticOrderService } from '../../services/diagnostic-order.service';
import { RouterManagerService } from "@ispm/core/services/router-manager.service";

const RESULTS_PATH = 'oncoSignal/';
const { get, set } = require('lodash');

@Component({
  selector: 'gen-signal-results',
  templateUrl: './signal-results.component.html',
  styleUrls: ['./signal-results.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SignalResultsComponent implements OnInit, OnDestroy {

  public content: string;
  private revisionId: string;
  currentOrder$: Observable<IDiagnosticOrder>;
  public arrowImgPath: string; // TODO: readonly
  public pcrCyclePath: string;
  public greenImgPath: string;
  public exclamationMarkPath: string;
  public payloadData$: Observable<any>;
  public tumorWarning: boolean;
  public orderId: string = '';
  public notes: number;
  public notesContent: any[];
  private _alive: boolean = true;
  private _pathwayGenes: Set<string> = new Set();
  public disclaimerMsg: string = "disclaimerMsg";
  public data:any;
  public diagnosticOrder:any;

  defaultPathwayWithComment: Array<PathwayWithComment> = [
    {
      gene: 'PI3K',
      class: 'PI3K_pathway',
      label: {
        class: 'PI3K_label',
        content: '*'
      },
      comment: {
        content: 'The PI3K score is derived from the inverse activity reading of the FOXO transcription factor.<br/>Please be aware that oxidative stress can induce FOXO activity, which may inadvertently lead to a low PI3K activity score.'
      }
    },
    {
      gene: 'MAPK',
      class: 'MAPK_pathway',
      label: {
        class: 'MAPK_label',
        content: '**'
      },
      comment: {
        content: 'The MAPK score is derived from the activity reading of the AP1 transcription factor.'
      }
    }
  ];

  /**
   * Multiple subscribers are going to use the same data object, it is better to use a BehaviorSubject here to multicast at once.
   * BehaviorSubject here is used so that the actual value can be accessed later on
   */
  pathwaysWithComment$: BehaviorSubject<Array<PathwayWithComment>> = new BehaviorSubject<Array<PathwayWithComment>>([]);
  sampleDataFields: { } [] =
    [{ 'value': 'tumorPercent', 'cssClass': 'percentageValue' },
    { 'value': 'dateOfTest' },
    { 'value': 'tissueType.display' },
    { 'value': 'location' },
    { 'value': 'additionalNotes' }];

  notesCount: number = 0; // TODO: Find a way to properly get note count. Most likely involves converting the notes service to angular2.

  constructor(private http: HttpInterceptorService,private diagnosticOrderActionsService: DiagnosticOrderActionsService,
    private dynamicModalLoader: DynamicModalLoaderService, private detector: ChangeDetectorRef,
    private diagnosticOrderService : DiagnosticOrderService, private pdfService: PdfService, private routerManagerService: RouterManagerService,
    private _store: Store<any>,
    private coreAC: CoreActionCreatorService) {
    this.arrowImgPath = '/assets/downloadbutton/combined-shape.svg';
    this.pcrCyclePath = '/assets/pcrwarning/terminal-cycling-conditions.svg';
    this.greenImgPath = '/assets/oksign/green-ok.svg';
    this.exclamationMarkPath = '/assets/exclamationmark/exclamation-mark.svg';
  }


  ngOnInit() {

    this._store.pipe(
      map(selectCurrentConfig('Test')),
      takeWhile(() => {
        return this._alive;
      }),
      map(config => {
        let activeConfig = config['pathwaysWithComment'] && config['pathwaysWithComment'].length
          ? config['pathwaysWithComment']
          : this.defaultPathwayWithComment;

        return this.pathwaysWithComment$.next(activeConfig)
      })
    ).subscribe();

    this.currentOrder$ = this.diagnosticOrderActionsService.getState()
      .pipe(
        takeWhile(() => this._alive),
        map(state => {
          this.tumorWarning = state.order.tumorWarning;
          this.orderId = state.order.id;
          this.notesContent = state.order.notesContent;
          this.detector.detectChanges();
          this.diagnosticOrder = state.order;
          return state.order;
        }));
    this.revisionId = this.routerManagerService.parameters?.revisionId;
    this.payloadData$ = this.getPayload().pipe(map(data => {
      if (data.pathwaySet && data.pathwaySet.samples && Array.isArray(data.pathwaySet.samples)) {
        lodash.forEach(data.pathwaySet.samples, sample => {
          lodash.forEach(sample.models, model => {
            const sampleModel = JSON.parse(model);
            this._pathwayGenes.add(sampleModel.pathway);
          });
        });
      }
      this.data = data;
      return data;
    }))
    .finally(() => {
      this.coreAC.setLoader(false);
    });
  }
  getPayload(): Observable<any> {
    return this.http.get(RESULTS_PATH + 'results', { revisionId: this.revisionId }).map(reponse => reponse.body[0]);
  }

  public getResult(payloadData): Array<string> {
    return payloadData.pathwaySet.samples[0].models;
  }

  public getQcMessage(payloadData): string {
    return (payloadData.pathwaySet.samples[0].qc_passed.toLowerCase() === 'true') ? 'Technical QC passed' : 'Technical QC failed';
  }

  public getQcFailReason(payloadData): string {
    return (this.isQcfailReason(payloadData)) ? payloadData.pathwaySet.samples[0].QC_fail_reason : undefined;
  }

  isQcfailReason(payloadData) {
    return payloadData.pathwaySet.samples[0].hasOwnProperty('QC_fail_reason') && payloadData.pathwaySet.samples[0].QC_fail_reason != "";
  }

  isQcPassed(payloadData) {
    return payloadData.pathwaySet.samples[0].qc_passed.toLowerCase() === 'true';
  }

  public getQcIconType(payloadData): string {
    return (payloadData.pathwaySet.samples[0].qc_passed.toLowerCase() === 'true') ? 'check' : 'error';
  }

  public getPcrData(payloadData): Array<string> {
    return payloadData.pcrData.reactDataList;
  }

  openPopup(payloadData) {
    const confirmDialog$ = this.dynamicModalLoader.popupModal(PlaceholderModalComponent, {
      title: 'PCR Results',
      payload: this.getPcrData(payloadData),
      buttons: {
        approveBtn: 'Close',
      },
      componentToLoad: PcrGraphComponent
    });
  }

  getWarningImage(order: IDiagnosticOrder & { file: { uploadedFile: { warning: string } } }): string {
    return order.file.uploadedFile.warning;
  }

  isPathwayCommentExist(gene: string) {
    return this._pathwayGenes.has(gene);
  }

  getGeneComment(geneModel: string): Observable<PathwayWithComment | any> {
    const model = JSON.parse(geneModel);
    return this.pathwaysWithComment$
      .pipe(
        takeWhile(() => this._alive),
        map(v => v.find(pwc => pwc.gene == model.pathway)),
        map(v => v ? v : {}));
  }

  getNotesContent(){
    if(this.notesContent!=undefined && this.getNotes() > 0){
    let notesText:string[]=[];
    let notes:[{}];
    this.notesContent.forEach((item)=>notesText.push(item.text.substring(3,item.text.length-4)));
    return notesText;
    }
  }

  getNotes() {
    this.diagnosticOrderActionsService.getState<number>('notes')
      .subscribe(data => {
        this.notes = data ? data : 0;
      });
    return this.notes;
  }

  ngOnDestroy(): void {
    this._alive = false;
  }

  downloadPdf(): any {
    this.diagnosticOrderService.getDiagnosticOrder(this.orderId).then((order) => {
      let docId = order.body.reportRef;
      const testNumber = order.body.identifiers[0].value;
      let fileName = testNumber + '.pdf';
      this.routerManagerService.reload();
      if (docId) {
        if (this.notes!= undefined && this.notes > 0) {
          return this.http.post(RESULTS_PATH + 'addNotesToPdf',  this.notesRequest(), true).map(reponse => reponse.body).subscribe(data => {
            const fileObj = { data: data };
            this.pdfService.downloadPDF(fileObj, fileName, docId);
          });
        } else{
          return this.http.get(RESULTS_PATH + 'getPdfBlob/' + docId, { revisionId: this.revisionId }).map(reponse => reponse.body).subscribe(data => {
            const fileObj = { data: data };
            this.pdfService.downloadPDF(fileObj, fileName, docId);
          });
        }

      } else {
        console.error('Could Not get Document Id');
      }
    }
    );
  }

  private notesRequest(){
    let body={
      data:this.data,
      diagnosticOrder:this.diagnosticOrder,
      revisionId:this.revisionId,
    }
    return body;
  }
}

export interface PathwayWithComment {
  gene: string;
  class?: string;
  label?: {
    class?: string;
    content?: string;
  };
  comment?: {
    class?: string;
    content?: string;
  };
}
